import { useRef, useState, useMemo, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  Text,
  useDisclosure,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccessCheck } from '@/hooks/useAccess'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

type TabValue = 'all' | 'trash'

const basePath = '/service-catalog/parent-ticket-statuses'

export default function ParentTicketStatusesPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const access = useAccessCheck()

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      showActionsColumn: true,
      dataFields: {
        code: { name: 'Code', withSort: true, maxWidth: '120px' },
        label: { name: 'Label', withSort: true, maxWidth: '200px' },
        is_closed: {
          name: 'Closed',
          withSort: true,
          maxWidth: '100px',
          badge: { true: 'green', false: 'gray' },
          badgeLabels: { true: 'Yes', false: 'No' },
        },
        is_on_hold: {
          name: 'On hold',
          withSort: true,
          maxWidth: '100px',
          badge: { true: 'orange', false: 'gray' },
          badgeLabels: { true: 'Yes', false: 'No' },
        },
      },
      dataSource: activeTab === 'trash' ? `${basePath}/archived` : basePath,
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: 'No parent ticket statuses found',
      hideSelection: false,
      otherActions: [],
    }),
    [activeTab, navigate]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const tableParams = useMemo(() => ({ search: searchQuery, trash: activeTab === 'trash' ? 1 : 0 }), [searchQuery, activeTab])

  const setSubSub = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    tableRef.current?.applyFilter('search', '')
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    tableRef.current?.applyFilter('search', searchQuery || '')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }
    setModalMessage(
      activeTab === 'trash' && bulkAction === 'delete'
        ? 'You are about to permanently delete these items. This action cannot be undone.'
        : 'Are you sure you want to apply this action?'
    )
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    setIsBulkProcessing(true)
    try {
      if (bulkAction === 'restore') {
        for (const id of selectedIds) {
          await apiClient.put(`${basePath}/restore/${id}`)
        }
        showSuccess('Success', selectedIds.length > 1 ? `${selectedIds.length} items restored` : 'Item restored successfully')
      } else if (bulkAction === 'delete' && activeTab === 'trash') {
        for (const id of selectedIds) {
          await apiClient.delete(`${basePath}/force-delete/${id}`)
        }
        showSuccess('Success', selectedIds.length > 1 ? `${selectedIds.length} items permanently deleted` : 'Item permanently deleted')
      } else if (bulkAction === 'delete' && activeTab === 'all') {
        for (const id of selectedIds) {
          await apiClient.delete(`${basePath}/${id}`)
        }
        showSuccess('Success', selectedIds.length > 1 ? `${selectedIds.length} items moved to trash` : 'Item moved to trash')
      }
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (error: unknown) {
      showError(error as Error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  return (
    <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
      <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
        <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
          <CardHeader flexShrink={0} py={3}>
            <Heading size="md" mb={1}>
              Parent Ticket Statuses
            </Heading>
            <Text fontSize="sm" color={colors.text}>
              Manage parent ticket statuses for the catalog
            </Text>
          </CardHeader>

          <CardHeader py={0} px={0} flexShrink={0}>
            <Box pt={2} pb={0}>
              <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange} variant="enclosed">
                  <TabList borderBottom="none">
                    <Tab>
                      All <Badge ml={2} colorScheme="blue">{totalRows}</Badge>
                    </Tab>
                    <Tab>
                      Trash <Badge ml={2} colorScheme="red">{totalTrash}</Badge>
                    </Tab>
                  </TabList>
                </Tabs>
                <Box pb={2}>
                  <ActionButtons
                    pathname={basePath}
                    createRoute={`${basePath}/create`}
                    createLabel="Create Parent Ticket Status"
                  />
                </Box>
              </HStack>
              <Box borderBottom="2px" borderColor={colors.border} mx={4} />
            </Box>
          </CardHeader>

          <CardHeader py={1} pt={3} pb={2} flexShrink={0} px={5}>
            <HStack spacing={4} flexWrap="wrap">
              {access.can_delete && (
                <HStack spacing={2} minW="250px">
                  <Select
                    placeholder="Bulk actions"
                    size="sm"
                    value={bulkAction}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setBulkAction(e.target.value)}
                  >
                    {activeTab === 'trash' && <option value="restore">Restore</option>}
                    {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                    {activeTab !== 'trash' && <option value="delete">Move to trash</option>}
                  </Select>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={handleBulkActionClick}
                    isLoading={isBulkProcessing}
                  >
                    Apply
                  </Button>
                </HStack>
              )}
              <Box flex="0 1 auto" minW="250px" maxW="400px">
                <InputGroup size="sm">
                  <Input
                    ref={searchRef}
                    placeholder="Search by code, label..."
                    value={searchQuery}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    pr="2.5rem"
                  />
                  <InputRightElement width="2.5rem">
                    <IconButton
                      aria-label="Search"
                      icon={<Search size={16} />}
                      size="sm"
                      h="1.75rem"
                      w="1.75rem"
                      onClick={handleSearch}
                      variant="ghost"
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>
            </HStack>
          </CardHeader>

          <CardBody p={0} pt={1} pb={4} mx={5} flex="1" overflow="hidden" display="flex" flexDirection="column" minH={0}>
            <Box h="100%" display="flex" flexDirection="column" minH={0}>
              <DataTable
                ref={tableRef}
                options={tableOptions}
                params={tableParams}
                setSubSub={setSubSub}
                access={permissions}
              />
            </Box>
          </CardBody>
        </Card>
      </VStack>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm</ModalHeader>
          <ModalBody>{modalMessage}</ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleConfirmBulkAction} isLoading={isBulkProcessing}>
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
